"use client";

import { useMemo, useState } from "react";
import InputFieldGrinwis from "components/General/InputFieldGrinwis/InputFieldGrinwis";
import NumberInputField from "components/General/NumberInputField/NumberInputField";
import ButtonGsfurn from "components/General/ButtonGsfurn/ButtonGsfurn";
import { printHTML } from "hooks/utils/formatter";
import CheckboxCard from "components/General/CheckboxCard/CheckboxCard";

const ErrorText = ({ name, errors }) => {
  const found_error = errors?.find((error) => error.field === name);
  if (!found_error) return null;
  return <p className="text-red-600 text-sm mt-1">{found_error.message}</p>;
};

export default function FormField({ field, feedbackMailSend }) {
  const { type, raw_name, labels, raw_values } = field || {};
  const baseType = useMemo(() => (type || "").replace("*", ""), [type]);
  const required = useMemo(() => (type || "").includes("*"), [type]);
  const label = labels?.[0] ?? raw_name;
  const [numberValue, setNumberValue] = useState(1);

  if (baseType === "submit") {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-end">
          <ButtonGsfurn
            color="yellow_grw"
            size="xl"
            type="submit"
          >
            {label}
          </ButtonGsfurn>
        </div>
        {feedbackMailSend?.message && (
          <div className="rounded-10 bg-gray-100 p-4 text-black">
            <p className="mb-2">{feedbackMailSend.message}</p>
            {!!feedbackMailSend?.fields?.length && (
              <div className="flex flex-col gap-1">
                {feedbackMailSend.fields.map((submission_error, field_index) => (
                  <span
                    key={field_index}
                    className="text-red-600"
                  >
                    {submission_error.field}: {submission_error.message}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (baseType === "raw_html") {
    return (
      <div className="prose max-w-none mt-4 rounded-10">
        {(raw_values ?? []).map((html_value, html_value_index) => (
          <div key={html_value_index}>{printHTML(html_value)}</div>
        ))}
      </div>
    );
  }

  if (baseType === "file") {
    return (
      <div className="flex flex-col gap-2 p-4 bg-body rounded-10">
        <label className="text-sm opacity-50">
          {label}
          {required ? " *" : ""}
        </label>
        <input
          name={raw_name}
          type="file"
          required={required}
          className="block w-full text-sm text-black"
        />
        <ErrorText
          name={raw_name}
          errors={feedbackMailSend?.fields}
        />
      </div>
    );
  }

  if (baseType === "checkbox") {
    const checkbox_options = raw_values ?? [];
    return (
      <div className="flex flex-col rounded-10 bg-body">
        <span className="text-sm opacity-50 p-4">
          {label}
          {required ? " *" : ""}
        </span>
        <div className="flex flex-wrap">
          {checkbox_options.map((checkbox_option, checkbox_option_index) => (
            <CheckboxCard
              key={checkbox_option_index}
              id={raw_name + "_" + checkbox_option_index}
              name={raw_name}
              onChange={() => {}}
            >
              {checkbox_option.endsWith("*") ? checkbox_option.slice(0, -1) : checkbox_option}
            </CheckboxCard>
          ))}
        </div>
        <ErrorText
          name={raw_name}
          errors={feedbackMailSend?.fields}
        />
      </div>
    );
  }

  if (baseType === "radio") {
    const radio_options = (raw_values ?? []).map((radio_value) =>
      radio_value.endsWith("*") ? radio_value.slice(0, -1) : radio_value
    );

    return (
      <div className="flex flex-col gap-2 bg-body rounded-10 p-4">
        <span className="text-sm opacity-50">
          {label}
          {required ? " *" : ""}
        </span>
        <div className="flex flex-wrap gap-4">
          {radio_options.map((radio_value, radio_value_index) => (
            <label
              key={radio_value_index}
              className="inline-flex items-center gap-2"
            >
              <input
                type="radio"
                name={raw_name}
                required={required}
              />
              <span className="text-sm">{radio_value}</span>
            </label>
          ))}
        </div>
        <ErrorText
          name={raw_name}
          errors={feedbackMailSend?.fields}
        />
      </div>
    );
  }

  if (baseType === "number") {
    return (
      <div className="flex flex-col gap-2 bg-body rounded-10 p-4">
        <label className="text-sm opacity-50">
          {label}
          {required ? " *" : ""}
        </label>
        <div className="flex items-center gap-4">
          <NumberInputField
            value={numberValue}
            onChange={(new_value) => {
              const number_value = typeof new_value === "string" ? Number(new_value) : new_value;
              setNumberValue(Number.isFinite(number_value) && number_value > 0 ? number_value : 1);
            }}
          />
          <input
            type="hidden"
            name={raw_name}
            value={String(numberValue)}
          />
        </div>
        <ErrorText
          name={raw_name}
          errors={feedbackMailSend?.fields}
        />
      </div>
    );
  }

  if (baseType === "date") {
    return (
      <InputFieldGrinwis
        type="date"
        name={raw_name}
        label={label + (required ? " *" : "")}
        required={required}
        customStylingOverride={{
          input: `bg-body rounded-10`,
        }}
      />
    );
  }

  if (baseType === "textarea") {
    return (
      <InputFieldGrinwis
        type="textarea"
        name={raw_name}
        label={label + (required ? " *" : "")}
        required={required}
        customStylingOverride={{
          input: `bg-body rounded-10`,
        }}
      />
    );
  }

  return (
    <InputFieldGrinwis
      type={baseType}
      name={raw_name}
      label={label + (required ? " *" : "")}
      required={required}
      customStylingOverride={{
        input: `bg-body rounded-10`,
      }}
    />
  );
}
